import Head from 'next/head';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Heading from 'shopper/components/Heading';
import Link from 'shopper/components/Link';

import { sendEvent } from 'lib/gtag';
import { toAbsoluteLink, toStoreCouponsLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { markup } from 'lib/utils';

const onButtonClickHandler = (storeName) => {
  sendEvent({
    action: 'store_coupons_item',
    category: 'sidebar_coupons',
    data: storeName,
  });
};

const jsonLdItemList = (stores) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: stores.map(({ storeName, storeSlug }, index) => ({
    '@type': 'SiteNavigationElement',
    '@id': '#navigation',
    name: storeName,
    url: toAbsoluteLink(toStoreCouponsLink(storeSlug)),
    position: index + 1,
  })),
});

const StoresWithFeaturedCoupons = forwardRef(
  (
    { className, linkHref = null, linkTitle = null, stores = [], ...rest },
    ref
  ) => {
    if (stores?.length === 0) {
      return null;
    }

    return (
      <>
        <Head>
          <script
            dangerouslySetInnerHTML={markup(
              JSON.stringify(jsonLdItemList(stores))
            )}
            type="application/ld+json"
          />
        </Head>
        <Box ref={ref} className={twMerge('p-4', className)} {...rest}>
          <Heading as="span" className="mb-4" size="size6">
            {placeholder('titles.highlightedCoupons')}
          </Heading>
          <div className="grid grid-cols-2 gap-4">
            {stores.map(({ storeName, storeSlug }) => (
              <div key={storeName} className="inline-flex w-full">
                <NextLink
                  href={toStoreCouponsLink(storeSlug)}
                  prefetch={false}
                  passHref
                >
                  <Button
                    as="a"
                    data-test-selector="goto-store-with-featured-coupons"
                    size="size3"
                    type="secondary-stroke"
                    full
                    onClick={() => {
                      onButtonClickHandler(storeName);
                    }}
                  >
                    {storeName}
                  </Button>
                </NextLink>
              </div>
            ))}
          </div>
          {linkTitle && (
            <div className="flex w-full items-center justify-center pt-4">
              <Link href={linkHref} size="size1">
                {linkTitle}
              </Link>
            </div>
          )}
        </Box>
      </>
    );
  }
);

StoresWithFeaturedCoupons.displayName = 'StoresWithFeaturedCoupons';

StoresWithFeaturedCoupons.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  linkHref: PropTypes.string,
  linkTitle: PropTypes.string,
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      storeName: PropTypes.string,
      storeSlug: PropTypes.string,
    })
  ),
};

export default StoresWithFeaturedCoupons;
